/**
 * ProjectClosureWizard Component
 *
 * 5-step wizard modal for formal project closure:
 * 1. Readiness Check
 * 2. Completion Checklist
 * 3. Client Signature
 * 4. Financial Summary + Feedback
 * 5. Confirmation
 *
 * ════════════════════════════════════════════════════════════════════════
 * ESTE WIZARD ERA FAIL-OPEN. No lo es más.
 * ════════════════════════════════════════════════════════════════════════
 *
 * `runValidation` tenía DOS ramas de fallback (la de `result.error` y la del
 * `catch`) que hardcodeaban `ready: true` + `completion_checklists_done: true`.
 * Un fallo de red, un rebote de RLS o el `RAISE EXCEPTION ... 42501` de la
 * compuerta de `validate_project_closure` (migración 20260731090000)
 * HABILITABAN el cierre completo: sin firma del cliente, con tareas abiertas y
 * con incidentes abiertos. El acto que lleva la firma del cliente se abría por
 * un fallo de infraestructura.
 *
 * Regla vigente: **si no se pudo validar, no se puede cerrar.** Y no es
 * overridable — el override existe para blockers CONOCIDOS (tareas abiertas,
 * falta la firma); ante un error de la RPC no se sabe nada del estado del
 * proyecto, así que no hay nada que un humano pueda decidir informadamente.
 *
 * El checklist tampoco es más teatro: era `DEFAULT_CHECKLIST` + `useState`
 * (estado local de React, nunca persistido, y la vieja línea 245 lo confesaba:
 * `return true; // Checklist is informational`). Ahora el paso 2 pinta el
 * checklist REAL de `project_checklists` — el mismo que siembra
 * `initiate_project_closure` en el carril MCP, desde la definición compartida
 * `src/constants/closureChecklist.json` — y cada tilde se persiste.
 *
 * Medido en prod (2026-07-30): `project_closures` = 0 filas. Nadie cerró nunca
 * un proyecto, así que nada de esto rompe un flujo en uso: es una compuerta
 * abierta que se cierra antes del primer cierre real.
 *
 * @module components/projects/ProjectClosureWizard
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Button,
  Alert,
  Spinner,
  Form,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import { formatCurrency } from '../../utils/formatters';
import {
  initiateClosure,
  saveSignature,
  completeClosure,
} from '../../services/closureService';
import {
  completeChecklistItem,
  uncompleteChecklistItem,
} from '../../services/checklistService';
import SignaturePad from '../common/SignaturePad';
import './ProjectClosureWizard.css';

const TOTAL_STEPS = 5;

const STEP_LABELS = [
  'Verificacion',
  'Checklist',
  'Firma',
  'Resumen',
  'Confirmacion',
];

/** Mensaje legible de cualquier cosa que haya llegado como error. */
const errorMessage = (err) =>
  err?.message || err?.error_description || (typeof err === 'string' ? err : '') ||
  'Error desconocido';

/** Ítems del checklist ordenados por `sort_order`, no por orden de llegada. */
const sortedItems = (checklist) =>
  [...(checklist?.items || [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  );

const ProjectClosureWizard = ({
  show,
  onHide,
  project,
  budgetSummary,
  onClosureComplete,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading] = useState(false);

  // Step 1: Readiness validation
  const [validationData, setValidationData] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [adminOverride, setAdminOverride] = useState(false);

  // Step 2: Checklist persistido (project_checklists + project_checklist_items)
  const [checklist, setChecklist] = useState(null);
  const [checklistError, setChecklistError] = useState(null);
  const [savingItemId, setSavingItemId] = useState(null);

  // Step 3: Signature
  const [signatureData, setSignatureData] = useState(null);
  const [signatureId, setSignatureId] = useState(null);
  const [signatureSaving, setSignatureSaving] = useState(false);

  // Step 4: Feedback
  const [customerRating, setCustomerRating] = useState(0);
  const [customerFeedback, setCustomerFeedback] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [completionSummary, setCompletionSummary] = useState('');

  // Step 5: Closing
  const [closureLoading, setClosureLoading] = useState(false);

  /**
   * Validar Y preparar el checklist cuando el wizard abre o el paso 1 está activo.
   *
   * FAIL-CLOSED: cualquier fallo deja `validationData` en null y
   * `validationError` puesto. No se fabrica un veredicto. Ver el bloque grande
   * al tope del archivo.
   */
  const runValidation = useCallback(async () => {
    if (!project?.id) return;

    setValidationLoading(true);
    setValidationError(null);
    setChecklistError(null);

    try {
      const result = await initiateClosure(project.id);

      if (result.error) {
        setValidationData(null);
        setChecklist(null);
        setValidationError(result.error);
        return;
      }

      const { validation, checklist: prepared, checklistError: prepareError } =
        result.data || {};

      if (!validation) {
        setValidationData(null);
        setChecklist(null);
        setValidationError(
          new Error('La verificacion de cierre no devolvio ningun resultado.')
        );
        return;
      }

      setValidationData(validation);
      setChecklist(prepared || null);
      setChecklistError(prepareError || null);
    } catch (err) {
      console.error('Validation error:', err);
      setValidationData(null);
      setChecklist(null);
      setValidationError(err);
    } finally {
      setValidationLoading(false);
    }
  }, [project?.id]);

  useEffect(() => {
    if (show && currentStep === 1) {
      runValidation();
    }
  }, [show, currentStep, runValidation]);

  /**
   * Reset wizard state when closed
   */
  useEffect(() => {
    if (!show) {
      setCurrentStep(1);
      setValidationData(null);
      setValidationError(null);
      setAdminOverride(false);
      setChecklist(null);
      setChecklistError(null);
      setSavingItemId(null);
      setSignatureData(null);
      setSignatureId(null);
      setCustomerRating(0);
      setCustomerFeedback('');
      setInternalNotes('');
      setCompletionSummary('');
    }
  }, [show]);

  /**
   * Handle signature save
   */
  const handleSignatureSave = async (data) => {
    setSignatureData(data);

    try {
      setSignatureSaving(true);
      const result = await saveSignature(project.id, data);

      if (result.error) {
        toast.error('Error al guardar la firma. Puede continuar de todas formas.');
        // Still allow proceeding
        setSignatureId(null);
      } else {
        setSignatureId(result.data.id);
        toast.success('Firma capturada exitosamente');
        // El ítem "Firma de aceptacion del cliente obtenida" del checklist ya
        // no se auto-tilda acá: el checklist es persistido, así que tildarlo
        // sería una escritura y quien la haga tiene que quedar registrado en
        // `completed_by`. Se tilda en el paso 2, como cualquier otro ítem.
      }
    } catch (err) {
      console.error('Signature save error:', err);
      toast.error('Error al guardar la firma');
    } finally {
      setSignatureSaving(false);
    }
  };

  /**
   * Handle final closure
   */
  const handleCompleteClosure = async () => {
    try {
      setClosureLoading(true);

      const result = await completeClosure(project.id, {
        customerRating: customerRating || null,
        customerFeedback: customerFeedback || null,
        internalNotes: internalNotes || null,
        completionSummary: completionSummary || null,
        clientSignatureId: signatureId,
        quotedAmount: budgetSummary?.quotedAmount || project.quoted_amount || 0,
        totalExpenses: budgetSummary?.totalExpenses || 0,
      });

      if (result.error) throw result.error;

      toast.success('Proyecto cerrado exitosamente');
      onClosureComplete(result.data);
      onHide();
    } catch (err) {
      console.error('Complete closure error:', err);
      toast.error(err.message || 'Error al cerrar el proyecto');
    } finally {
      setClosureLoading(false);
    }
  };

  /**
   * Tildar / destildar un ítem del checklist, PERSISTIDO.
   *
   * Deliberadamente NO es optimista: si la escritura falla (RLS, red), el tilde
   * no queda puesto en pantalla. Un tilde que se ve pero no se guardó es
   * exactamente el "teatro" que este componente tenía.
   */
  const handleToggleItem = async (item, checked) => {
    setSavingItemId(item.id);
    try {
      const result = checked
        ? await completeChecklistItem(item.id)
        : await uncompleteChecklistItem(item.id);

      if (result?.error) {
        toast.error(
          `No se pudo guardar el item del checklist: ${errorMessage(result.error)}`
        );
        return;
      }

      setChecklist((prev) => {
        if (!prev) return prev;
        const items = (prev.items || []).map((i) =>
          i.id === item.id ? { ...i, is_completed: checked } : i
        );
        return {
          ...prev,
          items,
          is_completed: checked ? items.every((i) => i.is_completed) : false,
        };
      });
    } catch (err) {
      console.error('Checklist item error:', err);
      toast.error(`No se pudo guardar el item del checklist: ${errorMessage(err)}`);
    } finally {
      setSavingItemId(null);
    }
  };

  /**
   * Can proceed to next step?
   */
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        // FAIL-CLOSED. Sin veredicto no se avanza, y el override NO aplica:
        // con la validación caída no se sabe si hay tareas abiertas ni si hay
        // firma, así que no hay decisión informada posible.
        if (validationError || !validationData) return false;
        // El checklist que no se pudo preparar SÍ es overridable: la validación
        // corrió, o sea que el estado real del proyecto se conoce.
        if (checklistError) return adminOverride;
        return validationData.ready || adminOverride;
      case 2: {
        // El checklist dejó de ser informativo. Si existe, se completa (o un
        // admin lo saltea explícitamente con el override del paso 1).
        const items = sortedItems(checklist);
        if (items.length === 0) return true;
        return items.every((i) => i.is_completed) || adminOverride;
      }
      case 3:
        return true; // Signature is captured in this step or already exists
      case 4:
        return true; // Feedback is optional
      case 5:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  /**
   * Render step progress indicator
   */
  const renderStepIndicator = () => (
    <div className="closure-wizard-steps">
      {Array.from({ length: TOTAL_STEPS }, (_, i) => {
        const step = i + 1;
        const isCompleted = step < currentStep;
        const isActive = step === currentStep;

        return (
          <div key={step} className="closure-step-indicator">
            {i > 0 && (
              <div className={`closure-step-line ${isCompleted ? 'completed' : ''}`} />
            )}
            <div
              className={`closure-step-circle ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              title={STEP_LABELS[i]}
            >
              {isCompleted ? <i className="bi bi-check"></i> : step}
            </div>
          </div>
        );
      })}
    </div>
  );

  /**
   * Step 1: Readiness Check
   */
  const renderStep1 = () => {
    if (validationLoading) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" className="mb-3" />
          <p className="text-muted">Verificando requisitos de cierre...</p>
        </div>
      );
    }

    // FAIL-CLOSED. Esta rama reemplaza al viejo fallback que fabricaba
    // `ready: true` y ofrecía "Puede continuar manualmente".
    if (validationError) {
      return (
        <>
          <h6 className="mb-3">Verificacion de Requisitos</h6>
          <Alert variant="danger" data-testid="closure-validation-error">
            <div>
              <i className="bi bi-shield-exclamation me-2"></i>
              <strong>
                No se pudo verificar el estado del proyecto, asi que el cierre
                queda bloqueado.
              </strong>
            </div>
            <div className="small mt-2">{errorMessage(validationError)}</div>
            <div className="small mt-2 mb-0 text-muted">
              El cierre lleva la firma del cliente: no se habilita sin saber si
              quedan tareas o incidentes abiertos. Reintente; si el error
              persiste, reportelo antes de cerrar.
            </div>
          </Alert>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={runValidation}
            data-testid="closure-validation-retry"
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Reintentar verificacion
          </Button>
        </>
      );
    }

    if (!validationData) {
      return (
        <>
          <h6 className="mb-3">Verificacion de Requisitos</h6>
          <Alert variant="warning" className="mb-0">
            Todavia no se verifico el estado del proyecto.
          </Alert>
        </>
      );
    }

    const checks = [
      {
        label: 'Tareas completadas',
        pass: validationData.open_tasks === 0,
        detail: validationData.open_tasks > 0
          ? `${validationData.open_tasks} tareas abiertas de ${validationData.total_tasks} totales`
          : `${validationData.completed_tasks} de ${validationData.total_tasks} completadas`,
      },
      {
        label: 'Incidentes resueltos',
        pass: validationData.open_incidents === 0,
        detail: validationData.open_incidents > 0
          ? `${validationData.open_incidents} incidentes abiertos`
          : 'Sin incidentes abiertos',
      },
      {
        label: 'Firma del cliente',
        pass: validationData.has_client_signature,
        detail: validationData.has_client_signature
          ? 'Firma capturada'
          : 'Se capturara en el paso 3',
      },
      {
        label: 'Checklists completados',
        pass: validationData.completion_checklists_done,
        detail: validationData.completion_checklists_done
          ? 'Todos los checklists requeridos completados'
          : 'Checklists pendientes',
      },
    ];

    return (
      <>
        <h6 className="mb-3">Verificacion de Requisitos</h6>

        {checks.map((check, i) => (
          <div key={i} className={`closure-check-item ${check.pass ? 'pass' : 'fail'}`}>
            <i
              className={`bi closure-check-icon ${
                check.pass ? 'bi-check-circle-fill pass' : 'bi-x-circle-fill fail'
              }`}
            />
            <div>
              <strong>{check.label}</strong>
              <div className="small text-muted">{check.detail}</div>
            </div>
          </div>
        ))}

        {checklistError && (
          <Alert variant="warning" className="mt-3 mb-0" data-testid="closure-checklist-error">
            <div>
              <i className="bi bi-list-check me-2"></i>
              <strong>No se pudo preparar el checklist de cierre.</strong>
            </div>
            <div className="small mt-2 mb-0">{errorMessage(checklistError)}</div>
          </Alert>
        )}

        {(!validationData.ready || checklistError) && (
          <div className="mt-3">
            <Alert variant="warning" className="mb-2">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Hay requisitos pendientes. Puede forzar el cierre con la opcion de abajo.
            </Alert>
            <Form.Check
              type="checkbox"
              label="Cerrar sin completar todos los requisitos (admin override)"
              checked={adminOverride}
              onChange={(e) => setAdminOverride(e.target.checked)}
              data-testid="closure-admin-override"
            />
          </div>
        )}

        {validationData.ready && !checklistError && (
          <Alert variant="success" className="mt-3 mb-0">
            <i className="bi bi-check-circle me-2"></i>
            El proyecto cumple con todos los requisitos para el cierre.
          </Alert>
        )}
      </>
    );
  };

  /**
   * Step 2: Completion Checklist
   */
  const renderStep2 = () => {
    const items = sortedItems(checklist);

    if (items.length === 0) {
      return (
        <>
          <h6 className="mb-3">Checklist de Finalización</h6>
          <Alert variant="warning" className="mb-0" data-testid="closure-checklist-missing">
            No hay un checklist de cierre preparado para este proyecto.
          </Alert>
        </>
      );
    }

    const completados = items.filter((i) => i.is_completed).length;

    return (
      <>
        <h6 className="mb-3">Checklist de Finalización</h6>
        <p className="text-muted small mb-3">
          Verifique cada elemento antes de cerrar el proyecto. Cada tilde se
          guarda contra el proyecto: es el mismo checklist que ve el agente.
        </p>

        {items.map((item) => (
          <div
            key={item.id}
            className="closure-checklist-item"
            data-testid={`closure-checklist-item-${item.id}`}
          >
            <Form.Check
              type="checkbox"
              id={`closure-checklist-${item.id}`}
              label={item.description}
              checked={!!item.is_completed}
              disabled={savingItemId === item.id}
              onChange={(e) => handleToggleItem(item, e.target.checked)}
            />
          </div>
        ))}

        <div className="mt-3 text-muted small">
          {completados} de {items.length} completados
        </div>
      </>
    );
  };

  /**
   * Step 3: Client Signature
   */
  const renderStep3 = () => {
    if (signatureData || validationData?.has_client_signature) {
      return (
        <>
          <h6 className="mb-3">Firma del Cliente</h6>
          <Alert variant="success">
            <i className="bi bi-check-circle me-2"></i>
            {signatureData
              ? `Firma capturada de: ${signatureData.signerName}`
              : 'Firma del cliente ya registrada en el sistema.'}
          </Alert>
          {signatureData?.signatureDataUrl && (
            <div className="text-center">
              <img
                src={signatureData.signatureDataUrl}
                alt="Firma del cliente"
                className="closure-signature-preview"
              />
              <div className="mt-2 small text-muted">
                {signatureData.signerName}
                {signatureData.signerRole && ` - ${signatureData.signerRole}`}
              </div>
            </div>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            className="mt-3"
            onClick={() => {
              setSignatureData(null);
              setSignatureId(null);
            }}
          >
            Capturar nueva firma
          </Button>
        </>
      );
    }

    return (
      <>
        <h6 className="mb-3">Firma del Cliente</h6>
        <p className="text-muted small mb-3">
          Capture la firma del representante del cliente como aceptacion del trabajo realizado.
        </p>
        {signatureSaving ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Guardando firma...
          </div>
        ) : (
          <SignaturePad
            onSave={handleSignatureSave}
            width={600}
            height={200}
          />
        )}
        <Alert variant="info" className="mt-3 mb-0">
          <small>
            <i className="bi bi-info-circle me-2"></i>
            La firma es opcional pero recomendada para el cierre formal del proyecto.
            Puede omitirla y continuar al siguiente paso.
          </small>
        </Alert>
      </>
    );
  };

  /**
   * Step 4: Financial Summary + Feedback
   */
  const renderStep4 = () => {
    // #679/#685: un proyecto sin monto cotizado no tiene "Variacion". Esto
    // mostraba "Monto Cotizado: 0" y "Variacion: 0" en verde sobre una obra que
    // simplemente nunca se cotizó — el mismo 0-por-decreto de las tools MCP.
    const quoted = budgetSummary?.quotedAmount ?? project?.quoted_amount ?? null;
    const spent = budgetSummary?.totalExpenses || 0;
    const variance = quoted == null ? null : quoted - spent;

    return (
      <>
        <h6 className="mb-3">Resumen Financiero</h6>

        <div className="closure-summary-section">
          <div className="closure-financial-row">
            <span>Monto Cotizado:</span>
            <span className="text-primary">{formatCurrency(quoted)}</span>
          </div>
          <div className="closure-financial-row">
            <span>Total Gastado:</span>
            <span>{formatCurrency(spent)}</span>
          </div>
          <div className="closure-financial-row">
            <span>Variacion:</span>
            <span className={variance == null ? 'text-muted' : variance >= 0 ? 'text-success' : 'text-danger'}>
              {formatCurrency(variance)}
            </span>
          </div>
        </div>

        <h6 className="mt-4 mb-2">Calificacion del Cliente</h6>
        <div className="star-rating mb-3">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              className={`star-rating-btn ${star <= customerRating ? 'active' : ''}`}
              onClick={() => setCustomerRating(star)}
              aria-label={`${star} estrellas`}
              data-testid={`closure-star-${star}`}
            >
              <i className={`bi ${star <= customerRating ? 'bi-star-fill' : 'bi-star'}`}></i>
            </button>
          ))}
          {customerRating > 0 && (
            <span className="ms-2 small text-muted">
              {customerRating}/5
            </span>
          )}
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="small">Retroalimentacion del Cliente</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={customerFeedback}
            onChange={(e) => setCustomerFeedback(e.target.value)}
            placeholder="Comentarios del cliente sobre el servicio..."
            data-testid="closure-customer-feedback"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="small">Notas Internas (Solo Admin)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Notas internas sobre el proyecto..."
            data-testid="closure-internal-notes"
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className="small">Resumen de lo Realizado</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={completionSummary}
            onChange={(e) => setCompletionSummary(e.target.value)}
            placeholder="Descripción breve de lo que se logró..."
            data-testid="closure-completion-summary"
          />
        </Form.Group>
      </>
    );
  };

  /**
   * Step 5: Confirmation
   */
  const renderStep5 = () => {
    // #679: mismo criterio que renderStep4 — sin monto cotizado se muestra
    // "-", no un 0 que se lee como "se cotizó en cero".
    const quoted = budgetSummary?.quotedAmount ?? project?.quoted_amount ?? null;
    const spent = budgetSummary?.totalExpenses || 0;

    return (
      <>
        <h6 className="mb-3">Confirmacion de Cierre</h6>

        <Alert variant="warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Esta accion es permanente. El proyecto sera marcado como cerrado y no podra ser modificado.
        </Alert>

        <div className="closure-summary-section">
          <h6>Proyecto</h6>
          <div><strong>{project?.project_number}</strong> - {project?.title}</div>
          {project?.client && (
            <div className="small text-muted">
              Cliente: {project.client.company_name || project.client.primary_contact_name}
            </div>
          )}
        </div>

        <div className="closure-summary-section">
          <h6>Financiero</h6>
          <div>Cotizado: {formatCurrency(quoted)} | Gastado: {formatCurrency(spent)}</div>
        </div>

        {signatureData && (
          <div className="closure-summary-section">
            <h6>Firma</h6>
            <div>{signatureData.signerName}</div>
            {signatureData.signerRole && (
              <div className="small text-muted">{signatureData.signerRole}</div>
            )}
          </div>
        )}

        {customerRating > 0 && (
          <div className="closure-summary-section">
            <h6>Calificacion</h6>
            <div>
              {[1, 2, 3, 4, 5].map((star) => (
                <i
                  key={star}
                  className={`bi ${star <= customerRating ? 'bi-star-fill text-warning' : 'bi-star text-muted'}`}
                  style={{ fontSize: '1rem', marginRight: '2px' }}
                ></i>
              ))}
              <span className="ms-2 small">{customerRating}/5</span>
            </div>
          </div>
        )}

        {completionSummary && (
          <div className="closure-summary-section">
            <h6>Resumen</h6>
            <div className="small">{completionSummary}</div>
          </div>
        )}

        <div className="closure-summary-section">
          <h6>Checklist</h6>
          <div className="small">
            {sortedItems(checklist).filter((c) => c.is_completed).length} de{' '}
            {sortedItems(checklist).length} items completados
          </div>
        </div>
      </>
    );
  };

  /**
   * Render current step content
   */
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      default:
        return null;
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="closure-wizard-modal"
      data-testid="project-closure-wizard"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-lock me-2"></i>
          Cerrar Proyecto - Paso {currentStep} de {TOTAL_STEPS}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {renderStepIndicator()}
        {renderStepContent()}
      </Modal.Body>

      <Modal.Footer>
        {currentStep > 1 && (
          <Button
            variant="outline-secondary"
            onClick={handleBack}
            disabled={closureLoading}
          >
            <i className="bi bi-chevron-left me-1"></i>
            Anterior
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={closureLoading}
        >
          Cancelar
        </Button>

        {currentStep < TOTAL_STEPS ? (
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={!canProceed() || loading}
          >
            Siguiente
            <i className="bi bi-chevron-right ms-1"></i>
          </Button>
        ) : (
          <Button
            variant="warning"
            onClick={handleCompleteClosure}
            disabled={closureLoading}
            data-testid="closure-confirm-button"
          >
            {closureLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Cerrando...
              </>
            ) : (
              <>
                <i className="bi bi-lock me-1"></i>
                Cerrar Proyecto
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

ProjectClosureWizard.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  project: PropTypes.object.isRequired,
  budgetSummary: PropTypes.shape({
    quotedAmount: PropTypes.number,
    totalExpenses: PropTypes.number,
    variance: PropTypes.number,
    variancePercentage: PropTypes.number,
  }),
  onClosureComplete: PropTypes.func.isRequired,
};

export default ProjectClosureWizard;
